import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { STRUTTURA_CONFIG } from "../config/strutturaConfig";
import {
  productGroupId,
  buildGroupResolver,
  countAdesioniByGroup,
  joinedGroupIds,
} from "../lib/risparmio/gruppi";
import {
  ShoppingCart,
  Package,
  Check,
  CheckCircle,
  Users,
  TrendingDown,
  Clock,
  Loader2,
  Info,
  Plus,
  Minus,
  Trash2,
  Tag,
  History,
  FileText,
  Truck,
  PackageCheck,
  XCircle
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "../components/ui/sheet";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ID struttura corrente (da configurazione)
const CURRENT_ZOHO_ACCOUNT_ID_LEGACY = STRUTTURA_CONFIG.zohoId; // Non usato - vedi strutturaId nel componente

// ID gruppo: basato sullo SKU del prodotto (vedi lib/risparmio/gruppi.js)

// Fallback immagini legacy (se non impostate da Admin → Listino Risparmio)
const PRODUCT_IMAGES = {
  "Pannoloni Mis. M": "/images/products/pannoloni.jpeg",
  "Pannoloni L/XL": "/images/products/pannoloni.jpeg",
  "Manopole Saponate": "/images/products/manopole.jpeg",
  "Manopole Non Saponate": "/images/products/manopole.jpeg",
  "Traverse 60x90": "/images/products/traverse.jpeg",
  "Traverse 80x180": "/images/products/traverse.jpeg",
  "Bavagli con tasca XL": "/images/products/bavagli.jpeg",
  "Guanti nitrile S.P.": "/images/products/guanti-nitrile.jpeg",
  "Candeggina": "/images/products/candeggina.jpeg",
  "Detersivo pav. Saniotec": "/images/products/saniotec.jpeg",
  "Tovaglioli monovelo": "/images/products/tovaglioli.jpeg",
  "Carta igienica 2 veli": "/images/products/carta-igienica.jpeg",
  "Detersivo Lemon stov": "/images/products/detersivo-lemon.jpeg",
  "Bicchieri carta 300cc": "/images/products/bicchieri.jpeg",
  "Siringhe 5ml": "/images/products/siringhe.jpeg",
  "Rotoloni multiuso 1600 strappi": "/images/products/rotoloni.jpeg"
};
const DEFAULT_PRODUCT_IMAGE = "/images/products/default.jpeg";

const resolveProductImage = (product) => {
  if (product?.image_url) {
    const url = product.image_url;
    if (url.startsWith("http")) return url;
    // Path statici (/images/...) sono serviti direttamente dal frontend
    if (url.startsWith("/images/")) return url;
    // Path dinamici del backend
    return `${API_URL}${url}`;
  }
  return PRODUCT_IMAGES[product?.name] || DEFAULT_PRODUCT_IMAGE;
};

// Minimo strutture aderenti
const MINIMO_STRUTTURE = 5;
const DEFAULT_SCADENZA = "28/02/2026";

// Helper per chiave univoca adesione
const buildJoinKey = (accountId, idGruppo, sku) => `${accountId}_${idGruppo}_${sku}`;
const getLocalStorageKey = (chiaveUnivoca) => `ga_joined_${chiaveUnivoca}`;

// Formatta unità
const formatPackUnit = (packQty, packUnit) => {
  if (packUnit === "L") return `${packQty} Litri`;
  if (packUnit === "ROTOLI") return `${packQty} Rotoli`;
  return `${packQty} ${packUnit}`;
};

export default function Risparmio() {
  const { token, user } = useAuth();
  
  // ID struttura dall'utente loggato (con fallback alla config)
  const strutturaId = user?.struttura_id || user?.zohoRecordId || STRUTTURA_CONFIG.zohoId;
  
  // Dati dal backend
  const [priceListData, setPriceListData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gruppi, setGruppi] = useState([]);
  // Ref ai gruppi correnti: evita che gli aggiornamenti dei contatori (setGruppi)
  // ricreino i callback e rilancino i fetch delle adesioni in loop.
  const gruppiRef = useRef(gruppi);
  gruppiRef.current = gruppi;
  // Cambia solo quando cambia il catalogo (id dei gruppi), non i contatori
  const catalogoKey = useMemo(() => gruppi.map(g => g.id).join("|"), [gruppi]);
  const [categorie, setCategorie] = useState(["Tutti"]);
  
  const [categoriaAttiva, setCategoriaAttiva] = useState("Tutti");
  const [tabAttiva, setTabAttiva] = useState("disponibili");
  
  // Stato adesioni
  const [joinedByKey, setJoinedByKey] = useState({});
  // Gruppi (id) a cui la struttura corrente ha aderito, a prescindere dal tier scelto
  const [joinedGroups, setJoinedGroups] = useState(() => new Set());
  const [loadingJoined, setLoadingJoined] = useState(true);
  
  // Dati struttura da Zoho
  const [strutturaData, setStrutturaData] = useState(null);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("join"); // "join" o "order"
  const [selectedGruppo, setSelectedGruppo] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [quantita, setQuantita] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // Carrello state
  const [carrello, setCarrello] = useState([]);
  const [carrelloOpen, setCarrelloOpen] = useState(false);

  // Cronologia ordini state
  const [ordini, setOrdini] = useState([]);
  const [loadingOrdini, setLoadingOrdini] = useState(false);
  const [showCronologia, setShowCronologia] = useState(false);
  
  // Risparmio effettivo dagli ordini
  const [risparmioEffettivo, setRisparmioEffettivo] = useState({ risparmio_totale: 0, num_ordini: 0 });
  
  // Modale adesioni
  const [showAdesioni, setShowAdesioni] = useState(false);
  const [listaAdesioni, setListaAdesioni] = useState([]);
  const [loadingAdesioni, setLoadingAdesioni] = useState(false);
  const [adesioniCount, setAdesioniCount] = useState(0);

  // Carica conteggio adesioni per il badge
  const fetchAdesioniCount = useCallback(async () => {
    if (!strutturaId) return; // Skip se non c'è ancora l'ID struttura
    try {
      const response = await axios.get(
        `${API_URL}/api/adesioni-gruppo/count/${strutturaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdesioniCount(response.data.count || 0);
    } catch (error) {
      console.error("Errore caricamento conteggio adesioni:", error);
    }
  }, [token, strutturaId]);

  // Carica conteggio adesioni all'avvio
  useEffect(() => {
    fetchAdesioniCount();
  }, [fetchAdesioniCount]);

  // Carica risparmio effettivo dagli ordini
  useEffect(() => {
    if (!strutturaId) return;
    const fetchRisparmioEffettivo = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/ordini/risparmio/${strutturaId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRisparmioEffettivo(response.data);
      } catch (error) {
        console.error("Errore caricamento risparmio:", error);
      }
    };
    fetchRisparmioEffettivo();
  }, [token, strutturaId]);

  // Carica dati struttura da Zoho
  useEffect(() => {
    if (!strutturaId) return; // Skip se non c'è ancora l'ID struttura
    const fetchStrutturaData = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/zoho/struttura/${strutturaId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStrutturaData(response.data);
      } catch (error) {
        console.error("Errore caricamento dati struttura:", error);
        // Fallback ai dati di configurazione
        setStrutturaData({
          nome: STRUTTURA_CONFIG.nome,
          email: STRUTTURA_CONFIG.email,
          telefono: STRUTTURA_CONFIG.telefono,
          nome_referente: STRUTTURA_CONFIG.referente_nome,
          telefono_referente: STRUTTURA_CONFIG.referente_telefono,
          nome_societa: STRUTTURA_CONFIG.ragione_sociale,
          partita_iva: STRUTTURA_CONFIG.partita_iva,
          codice_fiscale: STRUTTURA_CONFIG.codice_fiscale,
          indirizzo: STRUTTURA_CONFIG.indirizzo,
          cap: STRUTTURA_CONFIG.cap,
          comune: STRUTTURA_CONFIG.citta,
          provincia: STRUTTURA_CONFIG.provincia
        });
      }
    };
    fetchStrutturaData();
  }, [token, strutturaId]);

  // Sincronizza adesioni da Zoho all'avvio
  useEffect(() => {
    const syncAdesioniFromZoho = async () => {
      if (!strutturaId || gruppi.length === 0) return;
      
      try {
        const response = await axios.get(
          `${API_URL}/api/adesioni-gruppo/struttura/${strutturaId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const adesioni = response.data.adesioni || [];
        const newJoinedByKey = {};
        
        // Per ogni adesione, imposta la chiave come "joined"
        adesioni.forEach(adesione => {
          if (adesione.chiave_univoca) {
            newJoinedByKey[adesione.chiave_univoca] = true;
            // Salva anche in localStorage per persistenza
            localStorage.setItem(getLocalStorageKey(adesione.chiave_univoca), 'true');
          }
        });
        
        // Aggiorna stato joinedByKey se ci sono nuove adesioni
        if (Object.keys(newJoinedByKey).length > 0) {
          setJoinedByKey(prev => ({ ...prev, ...newJoinedByKey }));
        }
        
        // Gruppi a cui la struttura ha aderito (match per SKU, poi per id gruppo).
        // Il conteggio aderenti globale è calcolato in loadJoinedStatus.
        const giaAderiti = joinedGroupIds(adesioni, gruppiRef.current);
        if (giaAderiti.size > 0) {
          setJoinedGroups(prev => new Set([...prev, ...giaAderiti]));
        }
        
      } catch (error) {
        console.error("Errore sincronizzazione adesioni da Zoho:", error);
      }
    };
    
    syncAdesioniFromZoho();
  }, [strutturaId, gruppi.length, token]);

  // Carica listino dal backend
  useEffect(() => {
    const fetchPriceList = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/price-list`);
        setPriceListData(response.data);
        
        // Trasforma prodotti per la UI
        const products = response.data.products.map(p => ({
          id: productGroupId(p),
          nome: p.name,
          categoria: p.category,
          basePrice: p.base_price,
          maxDiscount: p.max_discount,
          tiers: p.tiers,
          defaultSku: p.default_sku,
          // Dati del tier default (primo/più piccolo)
          sku: p.tiers[0]?.sku || p.default_sku,
          packQty: p.tiers[0]?.pack_qty || 0,
          packUnit: p.tiers[0]?.pack_unit || "PZ",
          prezzo: p.tiers[0]?.unit_price || p.base_price,
          discountPct: p.tiers[0]?.discount_pct || 0,
          // Prezzo Gruppo Acquisto (38% di sconto sul prezzo base)
          prezzoGruppo: Math.round(p.base_price * 0.62 * 100) / 100,
          scontoGruppo: 38,
          immagine: resolveProductImage(p),
          // Stato gruppo acquisto
          aderenti: 0,
          minimo: MINIMO_STRUTTURE,
          scadenza: DEFAULT_SCADENZA,
          attivo: false
        }));
        
        setGruppi(products);
        
        // Estrai categorie
        const cats = new Set(products.map(p => p.categoria));
        setCategorie(["Tutti", ...Array.from(cats).sort()]);
        
      } catch (error) {
        console.error("Errore caricamento listino:", error);
        toast.error("Errore nel caricamento del listino prezzi");
      } finally {
        setLoading(false);
      }
    };
    
    fetchPriceList();
  }, []);

  // Carica cronologia ordini
  const fetchOrdini = useCallback(async () => {
    setLoadingOrdini(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/ordini/struttura/${strutturaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrdini(response.data.ordini || []);
    } catch (error) {
      console.error("Errore caricamento ordini:", error);
    } finally {
      setLoadingOrdini(false);
    }
  }, [token, strutturaId]);

  // Carica ordini quando si apre la cronologia
  useEffect(() => {
    if (showCronologia) {
      fetchOrdini();
    }
  }, [showCronologia, fetchOrdini]);

  // Carica carrello da localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('affidya_carrello');
    if (savedCart) {
      try {
        setCarrello(JSON.parse(savedCart));
      } catch (e) {
        console.error("Errore caricamento carrello:", e);
      }
    }
  }, []);

  // Salva carrello in localStorage
  useEffect(() => {
    localStorage.setItem('affidya_carrello', JSON.stringify(carrello));
  }, [carrello]);

  // Funzioni carrello
  const aggiungiAlCarrello = (gruppo, tier, qty = 1) => {
    // Usa il prezzo totale SENZA IVA (l'IVA viene aggiunta nel carrello)
    const prezzoSenzaIva = tier.total_price || (tier.num_packs * tier.unit_price) || tier.unit_price;
    const scontoFinale = tier.discount_pct || 0;
    
    const itemToAdd = {
      ...gruppo,
      sku: tier.sku,
      packQty: tier.pack_qty,
      packUnit: tier.pack_unit,
      numPacks: tier.num_packs || 1,
      unitPrice: tier.unit_price,  // Prezzo per singolo pacco
      prezzo: prezzoSenzaIva,  // Prezzo totale SENZA IVA
      vatRate: tier.vat_rate || gruppo.vatRate || 0,
      discountPct: scontoFinale,
      quantita: qty,
      isPrezzoGruppo: gruppo.usaPrezzoGruppo || false,
      notes: tier.notes || ""
    };
    
    setCarrello(prev => {
      const existing = prev.find(item => item.sku === tier.sku && item.isPrezzoGruppo === itemToAdd.isPrezzoGruppo);
      if (existing) {
        return prev.map(item => 
          item.sku === tier.sku && item.isPrezzoGruppo === itemToAdd.isPrezzoGruppo
            ? { ...item, quantita: item.quantita + qty }
            : item
        );
      }
      return [...prev, itemToAdd];
    });
    const tipoOrdine = gruppo.usaPrezzoGruppo ? "(PREZZO GRUPPO)" : "";
    toast.success(`${gruppo.nome} ${tipoOrdine} aggiunto al carrello`);
  };

  const rimuoviDalCarrello = (itemId) => {
    setCarrello(prev => prev.filter(item => `${item.sku}-${item.isPrezzoGruppo}` !== itemId));
  };

  const aggiornaQuantitaCarrello = (itemId, nuovaQty) => {
    if (nuovaQty < 1) {
      rimuoviDalCarrello(itemId);
      return;
    }
    setCarrello(prev => 
      prev.map(item => 
        `${item.sku}-${item.isPrezzoGruppo}` === itemId ? { ...item, quantita: nuovaQty } : item
      )
    );
  };

  const svuotaCarrello = () => {
    setCarrello([]);
    toast.info("Carrello svuotato");
  };

  // Carica adesioni della struttura corrente
  const fetchAdesioni = useCallback(async () => {
    if (!strutturaId) return; // Skip se non c'è ancora l'ID struttura
    setLoadingAdesioni(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/adesioni-gruppo/struttura/${strutturaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Arricchisci con info prodotto
      const resolveGruppo = buildGroupResolver(gruppi);
      const adesioniArricchite = (response.data.adesioni || []).map(adesione => {
        const gruppoId = resolveGruppo(adesione);
        const gruppo = gruppi.find(g => g.id === gruppoId);
        return {
          ...adesione,
          nome_prodotto: gruppo?.nome || adesione.id_gruppo,
          immagine: gruppo?.immagine || DEFAULT_PRODUCT_IMAGE
        };
      });
      
      setListaAdesioni(adesioniArricchite);
    } catch (error) {
      console.error("Errore caricamento adesioni:", error);
      toast.error("Errore nel caricamento delle adesioni");
    } finally {
      setLoadingAdesioni(false);
    }
  }, [token, gruppi, strutturaId]);

  // Elimina una singola adesione
  const handleDeleteAdesione = async (adesioneId, chiaveUnivoca) => {
    if (!window.confirm("Sei sicuro di voler annullare questa adesione?")) return;
    
    try {
      const response = await axios.delete(
        `${API_URL}/api/adesioni-gruppo/${adesioneId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Rimuovi dal localStorage
        if (chiaveUnivoca) {
          localStorage.removeItem(getLocalStorageKey(chiaveUnivoca));
        }
        
        // Aggiorna stato locale
        setJoinedByKey(prev => {
          const updated = { ...prev };
          delete updated[chiaveUnivoca];
          return updated;
        });
        
        // Aggiorna lista adesioni
        setListaAdesioni(prev => prev.filter(a => a.id !== adesioneId));
        
        // Decrementa contatore badge
        setAdesioniCount(prev => Math.max(0, prev - 1));
        
        // Decrementa contatore gruppo
        const adesione = listaAdesioni.find(a => a.id === adesioneId);
        const gruppoAdesione = adesione ? buildGroupResolver(gruppi)(adesione) : null;
        if (gruppoAdesione) {
          const restanti = listaAdesioni.filter(a => a.id !== adesioneId);
          if (!joinedGroupIds(restanti, gruppi).has(gruppoAdesione)) {
            setJoinedGroups(prev => {
              const next = new Set(prev);
              next.delete(gruppoAdesione);
              return next;
            });
          }
          setGruppi(prev => prev.map(g => {
            if (g.id === gruppoAdesione) {
              const nuoviAderenti = Math.max(0, g.aderenti - 1);
              return { ...g, aderenti: nuoviAderenti, attivo: nuoviAderenti >= g.minimo };
            }
            return g;
          }));
        }
        
        toast.success("Adesione annullata con successo");
      }
    } catch (error) {
      console.error("Errore eliminazione adesione:", error);
      toast.error(error.response?.data?.detail || "Errore nell'annullamento dell'adesione");
    }
  };

  // Carica adesioni quando si apre la modale
  useEffect(() => {
    if (showAdesioni && gruppi.length > 0) {
      fetchAdesioni();
    }
  }, [showAdesioni, fetchAdesioni, gruppi]);

  // Reset completo adesioni e carrello (mantenuto per debug)
  const resetTutto = () => {
    // Cancella tutte le chiavi di adesione dal localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('ga_joined_') || key === 'affidya_carrello' || key === 'affidya_listino_version')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Reset stato
    setCarrello([]);
    setJoinedByKey({});
    setJoinedGroups(new Set());
    setOrdini([]);
    
    // Reset contatori gruppi
    setGruppi(prev => prev.map(g => ({
      ...g,
      aderenti: 0,
      attivo: false
    })));
    
    toast.success("Dati resettati! Adesioni e carrello cancellati.");
  };

  const totaleCarrello = carrello.reduce((acc, item) => acc + (item.prezzo * item.quantita), 0);
  const itemsInCarrello = carrello.reduce((acc, item) => acc + item.quantita, 0);

  // Carica stato adesioni e conteggio per gruppo
  const loadJoinedStatus = useCallback(async () => {
    const gruppiCorrenti = gruppiRef.current;
    setLoadingJoined(true);
    const newJoinedByKey = {};
    
    // Carica da localStorage
    for (const gruppo of gruppiCorrenti) {
      const key = buildJoinKey(strutturaId, gruppo.id, gruppo.sku);
      const localKey = getLocalStorageKey(key);
      if (localStorage.getItem(localKey) === 'true') {
        newJoinedByKey[key] = true;
      }
    }
    
    setJoinedByKey(newJoinedByKey);
    
    // Carica tutte le adesioni per contare gli aderenti per gruppo
    // (/all-with-test è riservato al super admin: qui si usa /all, autenticato utente)
    try {
      const response = await axios.get(
        `${API_URL}/api/adesioni-gruppo/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const adesioni = response.data.adesioni || [];
      if (adesioni.length > 0) {
        const updatedJoined = { ...newJoinedByKey };
        
        for (const adesione of adesioni) {
          // Aggiorna stato "joined" per la struttura corrente
          if (adesione.struttura_id === strutturaId && adesione.chiave_univoca) {
            updatedJoined[adesione.chiave_univoca] = true;
            localStorage.setItem(getLocalStorageKey(adesione.chiave_univoca), 'true');
          }
        }
        
        setJoinedByKey(updatedJoined);
        setJoinedGroups(joinedGroupIds(adesioni, gruppiCorrenti, strutturaId));
        
        // Conteggio aderenti per gruppo: ogni adesione va al gruppo del proprio
        // prodotto (SKU), così prodotti diversi non vengono più sommati insieme.
        const aderentiPerGruppo = countAdesioniByGroup(adesioni, gruppiCorrenti);
        setGruppi(prev => prev.map(g => {
          const count = aderentiPerGruppo[g.id] || 0;
          return {
            ...g,
            aderenti: count,
            attivo: count >= g.minimo
          };
        }));
      }
    } catch (error) {
      console.error("Errore caricamento adesioni:", error);
    }
    
    setLoadingJoined(false);
  }, [token, strutturaId]);

  useEffect(() => {
    if (catalogoKey) {
      loadJoinedStatus();
    }
  }, [catalogoKey, loadJoinedStatus]);

  // Verifica se aderito
  const isJoined = (gruppo) => {
    const key = buildJoinKey(strutturaId, gruppo.id, gruppo.sku);
    return joinedByKey[key] === true || joinedGroups.has(gruppo.id);
  };

  // Apri modal per adesione
  const openJoinModal = (gruppo) => {
    if (isJoined(gruppo)) {
      toast.info("Hai già aderito a questo gruppo di acquisto.");
      return;
    }
    setSelectedGruppo(gruppo);
    setSelectedTier(gruppo.tiers[0]); // Default al primo tier
    setQuantita("1");
    setModalMode("join");
    setModalOpen(true);
  };

  // Apri modal per ordine diretto (prezzo listino)
  const handleOrdina = (gruppo) => {
    setSelectedGruppo(gruppo);
    setSelectedTier(gruppo.tiers[0]);
    setQuantita("1");
    setModalMode("order");
    setModalOpen(true);
  };

  // Apri modal per ordine al prezzo gruppo (promo attiva)
  const handleOrdinaGruppo = (gruppo) => {
    setSelectedGruppo({ ...gruppo, usaPrezzoGruppo: true });
    setSelectedTier(gruppo.tiers[0]);
    setQuantita("1");
    setModalMode("order");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setSelectedGruppo(null);
    setSelectedTier(null);
    setQuantita("");
    setModalMode("join");
  };

  // Submit adesione
  const submitJoin = async () => {
    if (!selectedGruppo || !selectedTier || submitting) return;
    
    const qty = parseInt(quantita, 10);
    if (isNaN(qty) || qty < 1) {
      toast.error("Inserisci una quantità valida (minimo 1)");
      return;
    }
    
    setSubmitting(true);
    
    const chiaveUnivoca = buildJoinKey(strutturaId, selectedGruppo.id, selectedTier.sku);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/adesioni-gruppo`,
        {
          struttura_id: strutturaId,
          id_gruppo_acquisto: selectedGruppo.id,
          sku_prodotto: selectedTier.sku,
          nome_prodotto: selectedGruppo.nome,
          quantita_mensile: qty,
          pack_qty: selectedTier.pack_qty,
          unit_price: selectedTier.unit_price,
          discount_pct: selectedTier.discount_pct,
          chiave_univoca: chiaveUnivoca
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setJoinedByKey(prev => ({ ...prev, [chiaveUnivoca]: true }));
        setJoinedGroups(prev => new Set([...prev, selectedGruppo.id]));
        localStorage.setItem(getLocalStorageKey(chiaveUnivoca), 'true');
        
        setGruppi(prev => prev.map(g => {
          if (g.id === selectedGruppo.id) {
            const nuoviAderenti = g.aderenti + 1;
            return { ...g, aderenti: nuoviAderenti, attivo: nuoviAderenti >= g.minimo };
          }
          return g;
        }));
        
        // Aggiorna conteggio badge
        setAdesioniCount(prev => prev + 1);
        
        toast.success("Hai aderito al gruppo di acquisto!");
        closeModal();
      } else if (response.data.error === "duplicate") {
        setJoinedByKey(prev => ({ ...prev, [chiaveUnivoca]: true }));
        setJoinedGroups(prev => new Set([...prev, selectedGruppo.id]));
        localStorage.setItem(getLocalStorageKey(chiaveUnivoca), 'true');
        toast.info("Hai già aderito a questo gruppo di acquisto.");
        closeModal();
      } else {
        toast.error(response.data.message || "Errore durante l'adesione");
      }
    } catch (error) {
      // Ensure errorMsg is a string before calling toLowerCase
      let errorMsg = error.response?.data?.detail || error.message || "";
      if (typeof errorMsg !== 'string') {
        errorMsg = JSON.stringify(errorMsg);
      }
      const errorMsgLower = errorMsg.toLowerCase();
      if (errorMsgLower.includes("duplicate") || errorMsgLower.includes("unique")) {
        setJoinedByKey(prev => ({ ...prev, [chiaveUnivoca]: true }));
        setJoinedGroups(prev => new Set([...prev, selectedGruppo.id]));
        localStorage.setItem(getLocalStorageKey(chiaveUnivoca), 'true');
        toast.info("Hai già aderito a questo gruppo di acquisto.");
        closeModal();
      } else {
        console.error("Errore adesione:", error.response?.data);
        toast.error("Errore tecnico. Riprova.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Conferma ordine dal modal
  const confermaOrdine = () => {
    if (!selectedGruppo || !selectedTier) return;
    const qty = parseInt(quantita, 10) || 1;
    aggiungiAlCarrello(selectedGruppo, selectedTier, qty);
    closeModal();
    setCarrelloOpen(true);
  };

  // Stato invio ordine
  const [invioOrdine, setInvioOrdine] = useState(false);

  // Invia ordine al backend (MongoDB + Zoho + Email)
  const inviaOrdine = async () => {
    if (carrello.length === 0 || invioOrdine) return;
    
    if (!strutturaData) {
      toast.error("Dati struttura non caricati. Riprova.");
      return;
    }
    
    setInvioOrdine(true);
    
    try {
      // Costruisci indirizzo sede legale/fatturazione
      const indirizzoFatturazione = strutturaData.via_fatturazione || "";
      
      // Costruisci indirizzo spedizione (indirizzo struttura)
      const indirizzoSpedizione = strutturaData.indirizzo 
        ? `${strutturaData.indirizzo}${strutturaData.numero_civico ? ` ${strutturaData.numero_civico}` : ""}`
        : "";
      
      const ordineData = {
        struttura_id: strutturaId,
        struttura_nome: strutturaData.nome || STRUTTURA_CONFIG.nome,
        cliente_email: strutturaData.email || STRUTTURA_CONFIG.email,
        cliente_telefono: strutturaData.telefono || STRUTTURA_CONFIG.telefono,
        // Referente ordine (da Zoho)
        referente_nome: strutturaData.nome_referente || strutturaData.responsabile_struttura,
        referente_telefono: strutturaData.telefono_referente,
        // Dati societari/fatturazione (da Zoho)
        ragione_sociale: strutturaData.nome_societa,
        partita_iva: strutturaData.partita_iva,
        codice_fiscale: strutturaData.codice_fiscale,
        pec: strutturaData.pec,
        codice_sdi: strutturaData.codice_sdi || strutturaData.codice_destinatario || strutturaData.sdi,
        indirizzo: indirizzoFatturazione,
        cap: strutturaData.cap_fatturazione,
        citta: strutturaData.citta_fatturazione,
        provincia: strutturaData.provincia_fatturazione,
        // Indirizzo spedizione (struttura)
        spedizione_indirizzo: indirizzoSpedizione,
        spedizione_cap: strutturaData.cap,
        spedizione_citta: strutturaData.comune,
        spedizione_provincia: strutturaData.provincia,
        // Items
        items: carrello.map(item => ({
          sku: item.sku || "",
          nome: item.nome || "",
          categoria: item.categoria || "altro",
          pack_qty: item.packQty || 1,
          pack_unit: item.packUnit || "PZ",
          prezzo: item.prezzo || 0,
          discount_pct: item.discountPct || 0,
          quantita: item.quantita || 1,
          iva: item.vatRate || 22
        })),
        note: ""
      };
      
      const response = await axios.post(
        `${API_URL}/api/ordini`,
        ordineData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(
          <div>
            <strong>Ordine #{response.data.ordine_id} confermato!</strong>
            <p className="text-sm mt-1">Totale: €{response.data.totale.toFixed(2)}</p>
            {response.data.email.success && (
              <p className="text-xs mt-1 text-green-600">Email di conferma inviata</p>
            )}
          </div>
        );
        svuotaCarrello();
        setCarrelloOpen(false);
      } else {
        toast.error("Errore nella creazione dell'ordine");
      }
    } catch (error) {
      console.error("Errore invio ordine:", error);
      toast.error(error.response?.data?.detail || "Errore nell'invio dell'ordine");
    } finally {
      setInvioOrdine(false);
    }
  };

  // Filtra gruppi
  const gruppiFiltrati = gruppi.filter(g => {
    if (categoriaAttiva !== "Tutti" && g.categoria !== categoriaAttiva) return false;
    if (tabAttiva === "attivi" && !g.attivo) return false;
    if (tabAttiva === "in-corso" && g.attivo) return false;
    return true;
  });

  // Statistiche
  const promoAttive = gruppi.filter(g => g.attivo).length;
  const inCorso = gruppi.filter(g => !g.attivo).length;
  
  // Estrai gli SKU dei prodotti a cui l'utente ha aderito
  const userAdesioniSku = listaAdesioni.map(a => a.sku);
  
  // Calcola il risparmio potenziale basato sulle adesioni effettive
  // Se l'utente ha aderito a prodotti, calcola il risparmio su quelli
  // Altrimenti mostra il risparmio potenziale massimo su tutti i prodotti
  const prodottiAderiti = gruppi.filter(g => userAdesioniSku.includes(g.sku));
  
  const totaleRisparmio = prodottiAderiti.length > 0 
    ? prodottiAderiti.reduce((acc, g) => {
        // Risparmio = prezzo base * sconto gruppo / 100 * quantità minima tipica (1 pack)
        const sconto = g.attivo ? g.scontoGruppo : g.maxDiscount;
        return acc + (g.basePrice * sconto / 100);
      }, 0)
    : gruppi.reduce((acc, g) => {
        // Se nessuna adesione, mostra potenziale su tutti
        if (g.maxDiscount > 0) {
          return acc + (g.basePrice * g.maxDiscount / 100);
        }
        return acc;
      }, 0);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4CAF50]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Page Header - responsive */}
      <PageHeader
        title="Risparmio insieme"
        subtitle="Risparmia aderendo ai gruppi di acquisto collettivi"
        icon={ShoppingCart}
        iconContainerClassName="bg-gradient-to-br from-green-500 to-green-600"
        iconClassName="text-white"
      />

      {/* Pulsanti Header */}
      <div className="flex justify-end mb-6">
        <div className="flex items-center gap-2">
          {/* Pulsante Adesioni con Badge */}
          <Button
            onClick={() => setShowAdesioni(true)}
            variant="outline"
            className="relative h-12 px-4 border-2 border-amber-400 text-amber-600 hover:bg-amber-50"
            data-testid="btn-adesioni"
            title="Visualizza le tue adesioni"
          >
            <Users className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">Adesioni</span>
            {adesioniCount > 0 && (
              <Badge className="absolute -top-2 -right-2 bg-amber-500 text-white h-6 w-6 flex items-center justify-center p-0 text-xs">
                {adesioniCount}
              </Badge>
            )}
          </Button>
          
          {/* Pulsante Cronologia */}
          <Button
            onClick={() => setShowCronologia(true)}
            variant="outline"
            className="h-12 px-4 border-2 border-slate-300 text-slate-600 hover:bg-slate-100"
            data-testid="btn-cronologia"
          >
            <History className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">Ordini</span>
          </Button>
          
          {/* Pulsante Carrello */}
          <Button
            onClick={() => setCarrelloOpen(true)}
            variant="outline"
            className="relative h-12 px-4 border-2 border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50]/10"
            data-testid="btn-carrello"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">Carrello</span>
            {itemsInCarrello > 0 && (
              <Badge className="absolute -top-2 -right-2 bg-[#4CAF50] text-white h-6 w-6 flex items-center justify-center p-0 text-xs">
                {itemsInCarrello}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="card-base bg-gradient-to-br from-[#4CAF50] to-[#43A047] text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm mb-1">
                  <TrendingDown className="w-4 h-4 inline mr-1" />
                  Hai risparmiato
                </p>
                <p className="text-3xl font-bold">€ {risparmioEffettivo.risparmio_totale?.toFixed(2) || "0.00"}</p>
                <p className="text-white/60 text-xs mt-1">
                  {risparmioEffettivo.num_ordini > 0 
                    ? `su ${risparmioEffettivo.num_ordini} ordin${risparmioEffettivo.num_ordini === 1 ? 'e' : 'i'} effettuat${risparmioEffettivo.num_ordini === 1 ? 'o' : 'i'}`
                    : "effettua il primo ordine!"}
                </p>
              </div>
              <TrendingDown className="w-12 h-12 text-white/20" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-promo-attive">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-bold text-[#4CAF50]">{promoAttive}</p>
            <p className="text-slate-500 font-medium">Promo attive</p>
            <p className="text-xs text-slate-400 mt-1">Ordina subito!</p>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-in-corso">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-bold text-amber-500">{inCorso}</p>
            <p className="text-slate-500 font-medium">In raccolta adesioni</p>
            <p className="text-xs text-slate-400 mt-1">Aderisci per attivare</p>
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="card-base mb-6 border-l-4 border-l-[#4CAF50]">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[#4CAF50] mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Come funziona?</h3>
              <ol className="text-sm text-slate-600 space-y-1">
                <li>1. <strong>Aderisci</strong> al gruppo di acquisto per il prodotto che ti interessa</li>
                <li>2. Quando il <strong>numero minimo di strutture</strong> aderisce, la promo si attiva</li>
                <li>3. <strong>Ordina</strong> il quantitativo e risparmia!</li>
              </ol>
              <p className="text-xs text-slate-400 mt-2">
                {priceListData?.supplier?.shipping_note || "Trasporto 3% sotto 500€"} • Prezzi IVA esclusa
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs e Filtri */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <Tabs value={tabAttiva} onValueChange={setTabAttiva}>
          <TabsList className="h-11">
            <TabsTrigger value="disponibili" className="px-4">Tutti</TabsTrigger>
            <TabsTrigger value="attivi" className="px-4">Promo attive</TabsTrigger>
            <TabsTrigger value="in-corso" className="px-4">In raccolta</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap gap-2">
          {categorie.map(cat => (
            <Button
              key={cat}
              variant={categoriaAttiva === cat ? "default" : "outline"}
              onClick={() => setCategoriaAttiva(cat)}
              className={`h-10 ${categoriaAttiva === cat ? "bg-[#4CAF50] hover:bg-[#43A047]" : ""}`}
              data-testid={`filter-${cat.toLowerCase()}`}
            >
              {cat}
            </Button>
          ))}
        </div>
      </div>

      {/* Grid Prodotti */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {gruppiFiltrati.map((gruppo, index) => {
          const joined = isJoined(gruppo);
          
          return (
            <Card 
              // L'id deriva dal nome prodotto: nel listino possono esserci nomi uguali (es. due
              // «Pannoloni made in IT»), quindi la key include anche sku/posizione.
              key={`${gruppo.id}:${gruppo.sku || gruppo.defaultSku || index}`}
              className={`card-base overflow-hidden transition-all relative ${
                gruppo.attivo ? "ring-2 ring-[#4CAF50] shadow-lg shadow-green-100" : ""
              } ${
                joined ? "ring-2 ring-amber-400 bg-amber-50/30" : ""
              }`}
              data-testid={`gruppo-card-${gruppo.sku}`}
            >
              {/* Indicatore "Hai aderito" - rimosso da qui, spostato sotto titolo */}
              
              {/* Banner promo attiva con animazione */}
              {gruppo.attivo && (
                <div className="bg-gradient-to-r from-[#4CAF50] to-[#2E7D32] text-white text-center py-2 text-sm font-semibold animate-pulse">
                  <Check className="w-4 h-4 inline mr-1" />
                  PROMO GRUPPO ATTIVA - Ordina al prezzo scontato!
                </div>
              )}
              
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-slate-600">
                      {gruppo.categoria}
                    </Badge>
                    {/* Badge sconto gruppo acquisto */}
                    {gruppo.attivo ? (
                      <Badge className="bg-green-500 text-white text-xs animate-pulse">
                        <Tag className="w-3 h-3 mr-1" />
                        -{gruppo.scontoGruppo}% ATTIVO
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-500 text-white text-xs">
                        <Tag className="w-3 h-3 mr-1" />
                        -{gruppo.scontoGruppo}% con gruppo
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    <Clock className="w-3 h-3" />
                    {gruppo.scadenza}
                  </div>
                </div>

                <div className="flex items-start gap-3 mb-4">
                  <div className="relative w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-slate-100">
                    <img 
                      src={gruppo.immagine} 
                      alt={gruppo.nome}
                      className={`w-full h-full object-cover ${joined ? "opacity-80" : ""}`}
                      onError={(e) => {
                        e.target.src = DEFAULT_PRODUCT_IMAGE;
                      }}
                    />
                    {/* Check overlay sull'immagine per adesioni */}
                    {joined && (
                      <div className="absolute inset-0 flex items-center justify-center bg-amber-500/40">
                        <CheckCircle className="w-8 h-8 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 leading-tight">{gruppo.nome}</h4>
                    <p className="text-xs text-slate-400 mt-1">
                      Da {formatPackUnit(gruppo.tiers[0]?.pack_qty, gruppo.tiers[0]?.pack_unit)}
                    </p>
                    {/* Badge "Hai aderito" sotto il titolo */}
                    {joined && (
                      <Badge className="mt-2 bg-amber-500 text-white text-xs flex items-center gap-1 w-fit">
                        <CheckCircle className="w-3 h-3" />
                        Hai aderito
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Sezione Prezzi - Mostra il prezzo del primo tier (pacco base) */}
                <div className="bg-slate-50 rounded-xl p-3 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-slate-500 mb-1">Prezzi a partire da</p>
                    <span className="text-2xl font-bold text-[#4CAF50]">
                      € {Math.min(...gruppo.tiers.map(t => t.unit_price)).toFixed(2).replace('.', ',')}/conf.
                    </span>
                    <p className="text-xs text-slate-400 mt-1">
                      + IVA {gruppo.vatRate || gruppo.tiers[0]?.vat_rate || 0}%
                    </p>
                  </div>
                </div>

                {/* Bottoni */}
                <div className="space-y-2">
                  {/* Pulsante Ordina Subito (funzionante) */}
                  <Button 
                    onClick={() => handleOrdina(gruppo)}
                    className="w-full h-11 bg-[#4CAF50] hover:bg-[#43A047] text-sm font-semibold"
                    data-testid={`btn-ordina-subito-${gruppo.sku}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Acquista
                  </Button>
                  
                  {/* Pulsante Aderisci - Disabilitato */}
                  <Button 
                    disabled
                    className="w-full h-10 bg-amber-50 text-amber-600 border border-amber-200 cursor-not-allowed text-xs font-medium"
                    data-testid={`btn-coming-soon-${gruppo.sku}`}
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    Gruppi d'acquisto - A breve disponibile
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {gruppiFiltrati.length === 0 && (
        <Card className="card-base">
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">Nessun prodotto trovato con i filtri selezionati</p>
          </CardContent>
        </Card>
      )}

      {/* Modal Adesione/Ordine con Tiers */}
      <Dialog open={modalOpen} onOpenChange={(open) => !submitting && setModalOpen(open)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {modalMode === "order" ? (
                <>
                  <ShoppingCart className="w-5 h-5 text-[#4CAF50]" />
                  {selectedGruppo?.usaPrezzoGruppo ? "Ordina al prezzo gruppo" : "Ordina prodotto"}
                </>
              ) : (
                <>
                  <Users className="w-5 h-5 text-[#4CAF50]" />
                  Aderisci al gruppo di acquisto
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedGruppo && (
            <div className="py-4">
              {/* Info prodotto */}
              <div className={`p-4 rounded-xl mb-4 ${selectedGruppo.usaPrezzoGruppo ? "bg-green-50 ring-1 ring-green-200" : "bg-slate-50"}`}>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-white">
                    <img 
                      src={selectedGruppo.immagine} 
                      alt={selectedGruppo.nome}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{selectedGruppo.nome}</p>
                    <p className="text-xs text-slate-500">{selectedGruppo.categoria}</p>
                    {selectedGruppo.usaPrezzoGruppo ? (
                      <Badge className="bg-green-500 text-white text-xs mt-1 animate-pulse">
                        🎉 PREZZO GRUPPO -{selectedGruppo.scontoGruppo}%
                      </Badge>
                    ) : selectedGruppo.maxDiscount > 0 && (
                      <Badge className="bg-red-500 text-white text-xs mt-1">
                        FINO A -{Math.round(selectedGruppo.maxDiscount)}%
                      </Badge>
                    )}
                  </div>
                </div>
                {selectedGruppo.usaPrezzoGruppo && (
                  <div className="mt-3 pt-3 border-t border-green-200 text-center">
                    <p className="text-sm text-green-700 font-medium">
                      Prezzo speciale attivo grazie alle {selectedGruppo.aderenti} strutture aderenti!
                    </p>
                  </div>
                )}
              </div>
              
              {/* Selezione Tier / Scaglioni - nascosto se prezzo gruppo */}
              {!selectedGruppo.usaPrezzoGruppo && (
              <div className="mb-4">
                <Label className="text-slate-700 font-semibold mb-3 block">
                  Sconti per quantità
                </Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedGruppo.tiers.map((tier) => (
                    <div
                      key={tier.sku}
                      onClick={() => setSelectedTier(tier)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedTier?.sku === tier.sku
                          ? "border-[#4CAF50] bg-[#4CAF50]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            selectedTier?.sku === tier.sku
                              ? "border-[#4CAF50]"
                              : "border-slate-300"
                          }`}>
                            {selectedTier?.sku === tier.sku && (
                              <div className="w-2 h-2 rounded-full bg-[#4CAF50]" />
                            )}
                          </div>
                          <div>
                            <span className="text-sm font-medium text-slate-700">
                              {tier.pack_qty} {tier.pack_unit} ({tier.num_packs || 1} {(tier.num_packs || 1) === 1 ? 'conf.' : 'conf.'})
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="font-bold text-[#4CAF50]">€ {tier.unit_price.toFixed(2)}/conf.</span>
                          {tier.discount_pct > 0 && (
                            <Badge className="bg-red-100 text-red-600 text-xs">
                              -{tier.discount_pct.toFixed(0)}%
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}
              
              {/* Riepilogo tier selezionato */}
              {selectedTier && (
                <div className={`p-3 rounded-lg mb-4 ${selectedGruppo.usaPrezzoGruppo ? "bg-green-100" : "bg-[#4CAF50]/10"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Hai selezionato:</span>
                    <div className="text-right">
                      <p className="font-bold text-[#4CAF50] text-lg">
                        € {selectedTier.unit_price.toFixed(2)}/conf.
                      </p>
                      <p className="text-xs text-slate-500">
                        {selectedTier.num_packs || 1} {(selectedTier.num_packs || 1) === 1 ? 'conf.' : 'conf.'} = € {(selectedTier.total_price || selectedTier.unit_price).toFixed(2)} + IVA {selectedTier.vat_rate || 0}%
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Input quantità */}
              <div className="space-y-2">
                <Label htmlFor="quantita" className="text-slate-700">
                  {modalMode === "order" ? (
                    <>Numero di confezioni <span className="text-red-500">*</span></>
                  ) : (
                    <>Quantità mensile stimata (confezioni) <span className="text-red-500">*</span></>
                  )}
                </Label>
                <Input
                  id="quantita"
                  type="number"
                  min="1"
                  value={quantita}
                  onChange={(e) => setQuantita(e.target.value)}
                  placeholder="Es: 1"
                  className="h-12 text-lg"
                  disabled={submitting}
                />
                {/* Helper per il calcolo quantità */}
                {selectedTier && quantita && parseInt(quantita, 10) > 0 && (() => {
                  const qty = parseInt(quantita, 10);
                  const numPacks = selectedTier.num_packs || 1;
                  const prezzoPerPacco = selectedTier.unit_price;
                  const prezzoTotaleSenzaIva = (selectedTier.total_price || prezzoPerPacco) * qty;
                  const totPezzi = qty * selectedTier.pack_qty;
                  return (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="text-sm text-blue-700 space-y-1">
                      <div className="flex justify-between">
                        <span>Ordine:</span>
                        <span><strong>{qty}</strong> × {selectedTier.pack_qty} {selectedTier.pack_unit} = <strong>{totPezzi} {selectedTier.pack_unit}</strong></span>
                      </div>
                      <div className="flex justify-between">
                        <span>Calcolo:</span>
                        <span>{qty} × {numPacks} conf. × € {prezzoPerPacco.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-blue-200">
                      <span className="text-blue-700 font-medium">Totale (IVA esclusa):</span>
                      <span className="font-bold text-blue-900 text-lg">
                        € {prezzoTotaleSenzaIva.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-blue-500 mt-1">+ IVA {selectedTier.vat_rate || 0}% al checkout</p>
                  </div>
                  );
                })()}
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={closeModal}
              disabled={submitting}
            >
              Annulla
            </Button>
            {modalMode === "order" ? (
              <Button
                onClick={confermaOrdine}
                disabled={!selectedTier || !quantita || parseInt(quantita, 10) < 1}
                className="bg-[#4CAF50] hover:bg-[#43A047]"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Aggiungi al carrello
              </Button>
            ) : (
              <Button
                onClick={submitJoin}
                disabled={submitting || !selectedTier || !quantita || parseInt(quantita, 10) < 1}
                className="bg-[#4CAF50] hover:bg-[#43A047]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Invio...
                  </>
                ) : (
                  "Conferma adesione"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sheet Carrello */}
      <Sheet open={carrelloOpen} onOpenChange={setCarrelloOpen}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-[#4CAF50]" />
              Il tuo carrello
              {itemsInCarrello > 0 && (
                <Badge className="bg-[#4CAF50] text-white ml-2">
                  {itemsInCarrello} {itemsInCarrello === 1 ? 'articolo' : 'articoli'}
                </Badge>
              )}
            </SheetTitle>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {carrello.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <ShoppingCart className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500 text-lg mb-2">Il carrello è vuoto</p>
                <p className="text-slate-400 text-sm">
                  Aggiungi prodotti dai gruppi di acquisto attivi
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {carrello.map((item) => {
                  const itemId = `${item.sku}-${item.isPrezzoGruppo}`;
                  return (
                  <div 
                    key={itemId} 
                    className={`flex gap-3 p-3 rounded-xl ${item.isPrezzoGruppo ? "bg-green-50 ring-1 ring-green-200" : "bg-slate-50"}`}
                    data-testid={`carrello-item-${item.sku}`}
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-white">
                      <img 
                        src={item.immagine} 
                        alt={item.nome}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = DEFAULT_PRODUCT_IMAGE;
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-slate-900 text-sm truncate">
                          {item.nome}
                        </h4>
                        {item.isPrezzoGruppo && (
                          <Badge className="bg-green-500 text-white text-[10px] px-1.5">
                            GRUPPO
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {item.packQty} {item.packUnit} ({item.numPacks || 1} {(item.numPacks || 1) === 1 ? 'conf.' : 'conf.'})
                      </p>
                      <p className="text-xs text-slate-400">
                        Tot. pezzi: {item.quantita * item.packQty} {item.packUnit}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`font-bold ${item.isPrezzoGruppo ? "text-green-600" : "text-[#4CAF50]"}`}>
                          € {item.prezzo.toFixed(2)}
                        </span>
                        <span className="text-xs text-slate-400">+ IVA {item.vatRate || 0}%</span>
                        {item.discountPct > 0 && (
                          <Badge className={`text-xs ${item.isPrezzoGruppo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                            -{item.discountPct.toFixed(0)}%
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <button
                        onClick={() => rimuoviDalCarrello(itemId)}
                        className="text-slate-400 hover:text-red-500 transition-colors p-1"
                        data-testid={`btn-rimuovi-${item.sku}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-1 bg-white rounded-lg border">
                        <button
                          onClick={() => aggiornaQuantitaCarrello(itemId, item.quantita - 1)}
                          className="p-1.5 hover:bg-slate-100 transition-colors rounded-l-lg"
                          data-testid={`btn-minus-${item.sku}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium">
                          {item.quantita}
                        </span>
                        <button
                          onClick={() => aggiornaQuantitaCarrello(itemId, item.quantita + 1)}
                          className="p-1.5 hover:bg-slate-100 transition-colors rounded-r-lg"
                          data-testid={`btn-plus-${item.sku}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {carrello.length > 0 && (() => {
            // Calcola subtotale senza IVA
            const subtotaleSenzaIva = carrello.reduce((acc, item) => acc + (item.prezzo * item.quantita), 0);
            
            // Calcola IVA raggruppata per aliquota
            const ivaPerAliquota = {};
            carrello.forEach(item => {
              const vatRate = item.vatRate || 0;
              const importo = item.prezzo * item.quantita;
              const iva = importo * vatRate / 100;
              if (!ivaPerAliquota[vatRate]) {
                ivaPerAliquota[vatRate] = 0;
              }
              ivaPerAliquota[vatRate] += iva;
            });
            
            // Totale IVA
            const totaleIva = Object.values(ivaPerAliquota).reduce((acc, val) => acc + val, 0);
            
            // Totale con IVA
            const totaleConIva = subtotaleSenzaIva + totaleIva;
            
            return (
            <SheetFooter className="border-t pt-4 mt-auto">
              <div className="w-full space-y-3">
                {/* Subtotale */}
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Subtotale (IVA esclusa)</span>
                  <span className="text-lg font-semibold text-slate-700">
                    € {subtotaleSenzaIva.toFixed(2)}
                  </span>
                </div>
                
                {/* Dettaglio IVA per aliquota */}
                <div className="bg-slate-50 rounded-lg p-2 space-y-1">
                  {Object.entries(ivaPerAliquota).map(([aliquota, importo]) => (
                    <div key={aliquota} className="flex justify-between text-xs text-slate-500">
                      <span>IVA {aliquota}%</span>
                      <span>€ {importo.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                
                {/* Totale con IVA */}
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-semibold text-slate-900">Totale (IVA inclusa)</span>
                  <span className="text-xl font-bold text-[#4CAF50]">
                    € {totaleConIva.toFixed(2)}
                  </span>
                </div>
                
                <p className="text-xs text-slate-400">
                  {priceListData?.supplier?.shipping_note || "Trasporto 3% sotto 500€"}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={svuotaCarrello}
                    className="w-full"
                    disabled={invioOrdine}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Svuota
                  </Button>
                  <Button
                    onClick={inviaOrdine}
                    disabled={invioOrdine}
                    className="w-full bg-[#4CAF50] hover:bg-[#43A047]"
                    data-testid="btn-conferma-ordine"
                  >
                    {invioOrdine ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Invio...
                      </>
                    ) : (
                      "Conferma ordine"
                    )}
                  </Button>
                </div>
              </div>
            </SheetFooter>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* Dialog Cronologia Ordini */}
      <Dialog open={showCronologia} onOpenChange={setShowCronologia}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#4CAF50]" />
              Cronologia Ordini
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingOrdini ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#4CAF50]" />
              </div>
            ) : ordini.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nessun ordine effettuato</p>
              </div>
            ) : (
              <div className="space-y-4">
                {ordini.map((ordine) => {
                  const statoConfig = {
                    confermato: { icon: FileText, color: "bg-blue-100 text-blue-700", label: "Confermato" },
                    in_elaborazione: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "In elaborazione" },
                    spedito: { icon: Truck, color: "bg-purple-100 text-purple-700", label: "Spedito" },
                    consegnato: { icon: PackageCheck, color: "bg-green-100 text-green-700", label: "Consegnato" },
                    annullato: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Annullato" }
                  };
                  const config = statoConfig[ordine.stato] || statoConfig.confermato;
                  const StatoIcon = config.icon;
                  
                  // Calcola risparmio ordine
                  const risparmioOrdine = ordine.items.reduce((acc, item) => {
                    if (item.discount_pct > 0) {
                      const prezzoOriginale = item.prezzo / (1 - item.discount_pct / 100);
                      return acc + (prezzoOriginale - item.prezzo) * item.quantita;
                    }
                    return acc;
                  }, 0);
                  
                  return (
                    <div 
                      key={ordine.ordine_id}
                      className="p-4 bg-slate-50 rounded-xl"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-slate-900">{ordine.ordine_id}</p>
                          <p className="text-xs text-slate-500">{ordine.data_ordine}</p>
                        </div>
                        <Badge className={config.color}>
                          <StatoIcon className="w-3 h-3 mr-1" />
                          {config.label}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 mb-3">
                        {ordine.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-slate-600">
                              {item.nome} x{item.quantita}
                              {item.discount_pct > 0 && (
                                <span className="text-red-500 ml-1">(-{item.discount_pct}%)</span>
                              )}
                            </span>
                            <span className="text-slate-900 font-medium">
                              €{(item.prezzo * item.quantita).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                        <div>
                          {risparmioOrdine > 0 && (
                            <span className="text-xs text-green-600 font-medium">
                              Risparmiato: €{risparmioOrdine.toFixed(2)}
                            </span>
                          )}
                        </div>
                        <span className="font-bold text-[#4CAF50]">
                          Totale: €{ordine.totale.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Adesioni */}
      <Dialog open={showAdesioni} onOpenChange={setShowAdesioni}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-500" />
              Le tue Adesioni
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingAdesioni ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : listaAdesioni.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-2">Non hai ancora aderito a nessun gruppo di acquisto</p>
                <p className="text-slate-400 text-sm">Aderisci ai gruppi per sbloccare i prezzi scontati!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {listaAdesioni.map((adesione) => (
                  <div 
                    key={adesione.id}
                    className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-200"
                    data-testid={`adesione-item-${adesione.id}`}
                  >
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-white">
                      <img 
                        src={adesione.immagine} 
                        alt={adesione.nome_prodotto}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = DEFAULT_PRODUCT_IMAGE;
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-slate-900 text-sm truncate">
                        {adesione.nome_prodotto}
                      </h4>
                      <p className="text-xs text-slate-500">
                        Quantità: {adesione.quantita} • SKU: {adesione.sku}
                      </p>
                      <Badge className={`text-xs mt-1 ${
                        adesione.stato === "In raccolta" 
                          ? "bg-amber-100 text-amber-700" 
                          : "bg-green-100 text-green-700"
                      }`}>
                        {adesione.stato || "In raccolta"}
                      </Badge>
                    </div>
                    <Button
                      onClick={() => handleDeleteAdesione(adesione.id, adesione.chiave_univoca)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                      data-testid={`btn-delete-adesione-${adesione.id}`}
                      title="Annulla adesione"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <p className="text-xs text-slate-400 mr-auto">
              {listaAdesioni.length} {listaAdesioni.length === 1 ? 'adesione' : 'adesioni'}
            </p>
            <Button
              variant="outline"
              onClick={() => setShowAdesioni(false)}
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
